/**
 * Build RosteringRouteSnapshot payloads from FixedRoute objects.
 */
import type { FixedRoute, ServiceDaysMask } from '../../types/fixedRoutes';
import type { RosteringRouteSnapshot } from './types';

type RouteWithOptionalTags = FixedRoute & { requiredVehicleTags?: string[] };

function serviceDaysToRecord(mask?: ServiceDaysMask): Record<string, boolean> | undefined {
  if (!mask) return undefined;
  return {
    monday: mask.monday,
    tuesday: mask.tuesday,
    wednesday: mask.wednesday,
    thursday: mask.thursday,
    friday: mask.friday,
    saturday: mask.saturday,
    sunday: mask.sunday,
  };
}

function inferTripDurationMinutes(route: FixedRoute): number | undefined {
  const schedules = route.tripSchedules || [];
  if (!schedules.length) return undefined;
  let max = 0;
  for (const trip of schedules) {
    const arrivals = trip.stopArrivalTimesMinutes || [];
    if (arrivals.length) {
      max = Math.max(max, Math.max(...arrivals));
    }
  }
  return max > 0 ? max : undefined;
}

export function fixedRouteToRosteringSnapshot(route: RouteWithOptionalTags): RosteringRouteSnapshot {
  const tripStarts = (route.tripSchedules || [])
    .map((t) => t.startTimeMinutes)
    .filter((n) => typeof n === 'number' && Number.isFinite(n));

  return {
    route_id: route.id,
    route_name: route.name,
    start_time: route.startTime,
    end_time: route.endTime,
    required_vehicle_tags: route.requiredVehicleTags || [],
    trip_start_minutes: tripStarts,
    trip_duration_minutes: inferTripDurationMinutes(route),
    service_days: serviceDaysToRecord(route.serviceDays),
  };
}

export function fixedRoutesToRosteringSnapshots(routes: FixedRoute[]): RosteringRouteSnapshot[] {
  return routes.map((r) => fixedRouteToRosteringSnapshot(r as RouteWithOptionalTags));
}

/** True when a fixed route has enough timing data for Advance Rostering demand. */
export function isRouteEligibleForRostering(route: FixedRoute): boolean {
  return (
    (!!route.startTime && !!route.endTime) ||
    (Array.isArray(route.tripSchedules) && route.tripSchedules.length > 0)
  );
}

